export interface AuthUser {
  id?: string;
  uid?: string;
  firstname?: string;
  middlename?: string;
  lastname?: string;
  type?: string;
  permissions?: string[];
}
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthTokenModel} from '../shared/interfaces/data.interfaces';
import {firstValueFrom, lastValueFrom, Subscription, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {StorageService} from './storage-service.service';
import {Dialog} from '@angular/cdk/dialog';
import { NotificationService } from './notification.service';
import { GraphqlService } from 'src/app/services/graphql.service';
import { gql } from 'apollo-angular';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { environment } from '../../environments/environment';
import { Injectable, OnDestroy } from '@angular/core';
const CHECK_USERNAME_AVAILABILITY = gql`
  query CheckUsernameAvailability($username: String!) {
    checkUsernameAvailability(username: $username)
  }
`;

const REGISTER_ADMIN = gql`
  mutation RegisterAdmin($input: RegisterAdminInput!) {
    registerAdmin(input: $input) {
      token
      refreshToken
      response {
        message
        status
      }
    }
  }
`;

const VERIFY_EMAIL = gql`
  mutation VerifyEmail($token: String!) {
    verifyEmail(token: $token) {
      token
      refreshToken
      response {
        message
      }
    }
  }
`;

const RESEND_VERIFICATION_EMAIL = gql`
  mutation ResendVerificationEmail($email: String!) {
    resendVerificationEmail(email: $email) {
      response {
        message
      }
    }
  }
`;

const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email) {
      response {
        message
      }
    }
  }
`;

const CONFIRM_PASSWORD_RESET = gql`
  mutation ConfirmPasswordReset($email: String!, $code: String!, $newPassword: String!) {
    confirmPasswordReset(email: $email, code: $code, newPassword: $newPassword) {
      token
      refreshToken
      response {
        message
        status
      }
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {

  subscriptions: Subscription = new Subscription();
  authUser: AuthUser;

  private errorCode: any;
  private errorDescription: any;

  constructor(
    private dialog: Dialog,
    private http: HttpClient,
    private router: Router,
    private snackbar: MatSnackBar,
    private permissionsService: NgxPermissionsService,
    private storageService: StorageService,
    private notificationService:NotificationService,
    private apollo : GraphqlService
  ) {
  }

  /**
   * Exchanges the stored refresh token for a fresh access token via
   * Djoser/SimpleJWT's standard endpoint. Access tokens only last 30
   * minutes (SIMPLE_JWT.ACCESS_TOKEN_LIFETIME) — without this, every
   * session would hard-expire mid-use. Refresh tokens don't rotate
   * (ROTATE_REFRESH_TOKENS isn't set), so only `access` comes back.
   * Throws if the refresh token itself is missing/expired/invalid —
   * callers should treat that as "the session is truly over."
   */
  async refreshAccessToken(): Promise<string> {
    const refreshToken = this.storageService.get('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response: any = await firstValueFrom(
      this.http.post(`${environment.SERVER_URL}/auth/jwt/refresh/`, { refresh: refreshToken })
    );

    const newAccessToken = response?.access;
    if (!newAccessToken) {
      throw new Error('Refresh response did not include an access token');
    }

    const cleanToken = newAccessToken.startsWith('Bearer ')
      ? newAccessToken.replace('Bearer ', '')
      : newAccessToken;

    this.storageService.set('currentClient', cleanToken);
    return cleanToken;
  }

  async login(formData: any): Promise<AuthTokenModel> {
    localStorage.removeItem('tempClient');

    const body = {
      username: formData.username,
      password: formData.password,
      // Extends the issued refresh token's lifetime server-side (see
      // core.serializers.EmailVerifiedTokenObtainPairSerializer) — opt-in,
      // so a shared/public device doesn't silently get a month-long session.
      remember_me: !!formData.rememberMe,
      // scopes: "read,write"
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });

    const loginUrl = environment.SERVER_URL + `/auth/jwt/create/`;

    return this.http
      .post<AuthTokenModel>(loginUrl, body, { headers })
      .pipe(
        map(apiResponse => {
          console.log('token details access', apiResponse);
          // @ts-ignore
          const tokenDetails = apiResponse;


          if (tokenDetails && tokenDetails.access) {
            console.log(tokenDetails.access, 'all the details are being present');
            const cleanToken = tokenDetails.access.startsWith('Bearer ')
              ? tokenDetails.access.replace('Bearer ', '')
              : tokenDetails.access;

            this.storageService.set('currentClient', cleanToken);
            this.storageService.set('refreshToken', tokenDetails.refresh);
            this.storageService.set('expireTime', tokenDetails.accessTokenExpiresIn);
            // @ts-ignore
          }

          return apiResponse;
        }),
        // 2. Error Handling
        catchError((httpError: HttpErrorResponse) => {
          let errorMessage = 'An unknown network error occurred.';
          const statusCode = httpError.status; // Capture the status code here

          if (statusCode === 0) {
            // Client-side or network error (status 0)
            errorMessage = 'A network connection error occurred. Please check your internet connection.';
          } else {
            errorMessage = httpError.error?.detail || httpError.message || 'An error occurred during login.';
          }

          // Create the custom error object using the captured status code
          // @ts-ignore
          const customError: CustomAuthError = {
            statusCode: statusCode,
            // Ensure any potential whitespace is removed
            message: errorMessage.trim(),
            // Distinguishes "wrong credentials" from "correct credentials,
            // unverified email" so callers can route to the right screen.
            code: httpError.error?.code,
            // Only present for email_not_verified — the backend includes it
            // because the password just verified correctly (so revealing it
            // isn't an account-existence leak), and the login field accepts
            // a username too, so the client can't otherwise be sure what
            // address to resend the verification link to.
            email: httpError.error?.email
          };

          this.notificationService.errorMessage(customError.message);

          // Re-throw the custom error to reject the Promise
          return throwError(() => customError);
        })
      )
      .toPromise();
  }


  async tempToken(): Promise<AuthTokenModel> {

    localStorage.removeItem('tempClient');
    const body = `grant_type=client_credentials`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      // Authorization: 'Basic ' + btoa(environment.CLIENT_ID + ':' + environment.CLIENT_SECRET),
    });
    return lastValueFrom(
      this.http
        .post<AuthTokenModel>(environment.SERVER_URL + `/auth/token`, body, { headers })
        .pipe(
          map(tokenDetails => {
            if (tokenDetails && tokenDetails.access) {
              localStorage.setItem('tempClient', tokenDetails.access);
            }
            return tokenDetails;
          })
        )
    );
  }

  async getTranslations(): Promise<AuthTokenModel> {

    localStorage.removeItem('tempClient');
    const body = `grant_type=client_credentials`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      // Authorization: 'Basic ' + btoa(environment.CLIENT_ID + ':' + environment.CLIENT_SECRET),
    });
    return await lastValueFrom(
      this.http
        .post<AuthTokenModel>(environment.SERVER_URL + `/auth/login`, body, { headers })
        .pipe(
          map(tokenDetails => {
            if (tokenDetails && tokenDetails.access) {
              localStorage.setItem('tempClient', tokenDetails.access);
            }
            return tokenDetails;
          })
        )
    );
  }

  async getUserPermissions(): Promise<string[]> {
    try {
      const stored = this.storageService.get('permissionCodenames');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /** Convenience getter for the current user's ShopUser UUID */
  getShopUserUuid(): string | null {
    return this.storageService.get('shopUserUuid') || null;
  }


  /**
   * Instantly reload stored permissions into ngx-permissions.
   * Called at the TOP of authRole() so guards work on page refresh
   * before the async API response arrives.
   */
  rehydratePermissions(): void {
    try {
      const stored = this.storageService.get('permissionCodenames');
      if (stored) {
        const codenames: string[] = JSON.parse(stored);
        if (codenames.length > 0) {
          this.permissionsService.loadPermissions(codenames);
          console.log('[Auth] Rehydrated permissions from storage:', codenames);
        }
      }
    } catch {
      // storage parse error — will be refreshed by API call
    }
  }

  async authRole() {
    const perms = this.storageService.get('currentClient');

    // Fast-path: reload from storage immediately so *ngxPermissionsOnly
    // directives work before the API response comes back
    this.rehydratePermissions();

    if (perms) {

    try {
          const result: any = await this.apollo.fetchData({
            query: GET_CURRENT_USER_PROFILE,
            variables: {},
          });

          const userDetails = result?.data?.getCurrentUserInfo;

          const authUser: AuthUser = {
            id: userDetails?.userId,
            uid: userDetails?.uid,
            firstname: userDetails?.firstName,
            middlename: userDetails?.lastName,
            lastname: userDetails?.lastName,
            type: userDetails?.shopRole,
            permissions: userDetails?.permissionCodenames ?? []
          };

          // ── Persist user details to storage ───────────────────────────────
          this.storageService.set('account', userDetails?.userId);
          this.storageService.set('shop', userDetails?.shopName);
          this.storageService.set('userType', userDetails?.shopRole);
          this.storageService.set('firstName', userDetails?.firstName);
          this.storageService.set('middleName', userDetails?.middleName);
          this.storageService.set('lastName', userDetails?.lastName);
          this.storageService.set('email', userDetails?.email);
          this.storageService.set('username', userDetails?.username);
          this.storageService.set('phone', userDetails?.phone);
          this.storageService.set('id', userDetails?.userId);
          this.storageService.set('photoUrl', userDetails?.photoUrl);
          this.storageService.set('shopUserUuid', userDetails?.shopUuid);

          // ── Permissions: persist + load into ngx-permissions ─────────────
          const codenames: string[] = userDetails?.permissionCodenames ?? [];
          const roleUuids: string[]  = userDetails?.roleUuids ?? [];
          this.storageService.set('permissionCodenames', JSON.stringify(codenames));
          this.storageService.set('roleUuids', JSON.stringify(roleUuids));

          // Load into ngx-permissions so *ngxPermissionsOnly directives work
          this.permissionsService.flushPermissions();
          this.permissionsService.loadPermissions(codenames);
          console.log('[Auth] Loaded permissions:', codenames);

        } catch (error) {
          console.error(error);
          this.notificationService.errorMessage('Failed to get User info');
        }
      }

    console.log('No permissions found in storage');
  }


  logout(): any {
    const accessToken = this.storageService.get('currentClient');
    const refreshToken = this.storageService.get('refreshToken');
    const body = `accessToken=${encodeURIComponent(accessToken)}&refreshToken=${encodeURIComponent(refreshToken)}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: 'Bearer ' + encodeURIComponent(accessToken),
    });
    this.storageService.clear();
    localStorage.clear();
    sessionStorage.clear();
    this.permissionsService.flushPermissions();
    this.subscriptions.add(
      this.http
        .post<any>(environment.SERVER_URL + `/auth/logout`, body, { headers })
        .subscribe(() => {
          // this.openSnackbar('Logout Successfully');
          this.notificationService.message('logout successfully');
        }
          , error => this.collectFailedRequest(error))
    );
  }

  passwordReset(formData: any) {
    this.subscriptions.add(
      this.http.post(environment.SERVER_URL + '/auth/reset-password', formData)
        .subscribe(() => {
            this.openSnackbar('Password Changed Successfully');
            this.router.navigate(['', 'login']);
          }, error => {

          this.openSnackbar(error?.error?.message);
          }
        )
    );
  }


  changepassword(formData: any) {
    this.subscriptions.add(
      this.http.post(environment.SERVER_URL + '/auth/update-password', formData)
        .subscribe(() => {
            this.openSnackbar('Password Changed Successfully');
            this.router.navigate(['', 'login']);
          }, error => {

          this.openSnackbar(error?.error?.message);
          }
        )
    );
  }

  async checkUsernameAvailability(username: string): Promise<boolean> {
    try {
      const result: any = await this.apollo.fetchData({
        query: CHECK_USERNAME_AVAILABILITY,
        variables: { username }
      });

      const isAvailable = result?.data?.checkUsernameAvailability;

      // Convert string "true"/"false" to boolean
      if (typeof isAvailable === 'string') {
        return isAvailable.toLowerCase() === 'true';
      }

      return isAvailable === true;
    } catch (error) {
      return false;
    }
  }

  async registerAdmin(formData: any): Promise<any> {
    try {
      const result: any = await this.apollo.mutate({
        mutation: REGISTER_ADMIN,
        variables: {
          input: {
            username: formData.username,
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
            shopName: formData.shopName,
            shopAddress: formData.shopAddress,
            shopPhone: formData.shopPhone,
            shopEmail: formData.shopEmail,
            referralCode: formData.referralCode || null,
            agreeToTerms: formData.agreeToTerms,
          }
        }
      });

      const response = result?.data?.registerAdmin;

      if (response?.token) {
        const cleanToken = response.token.startsWith('Bearer ')
          ? response.token.replace('Bearer ', '')
          : response.token;

        this.storageService.set('currentClient', cleanToken);
        this.storageService.set('refreshToken', response.refreshToken);

        this.notificationService.successMessage(
          response.response?.message || 'Admin account and shop created successfully!'
        );

        await this.authRole();
        this.router.navigate(['/dashboard']);
      } else if (response?.response?.status) {
        // Registered, but the account needs email verification before login.
        this.notificationService.successMessage(
          response.response?.message || 'Check your email to verify your account.'
        );
        this.router.navigate(['/verify-email-pending'], {
          queryParams: { email: formData.email }
        });
      } else {
        this.notificationService.errorMessage(
          response?.response?.message || 'Registration failed'
        );
      }

      return response;
    } catch (error: any) {
      console.error('Registration error:', error);
      const message = error?.graphQLErrors?.[0]?.message
        || error?.message
        || 'Registration failed. Please try again.';
      this.notificationService.errorMessage(message);
      throw error;
    }
  }

  /** Confirms an emailed verification token and logs the user in on success. */
  async verifyEmail(token: string): Promise<any> {
    const result: any = await this.apollo.mutate({
      mutation: VERIFY_EMAIL,
      variables: { token }
    });

    const response = result?.data?.verifyEmail;

    if (response?.token) {
      const cleanToken = response.token.startsWith('Bearer ')
        ? response.token.replace('Bearer ', '')
        : response.token;

      this.storageService.set('currentClient', cleanToken);
      this.storageService.set('refreshToken', response.refreshToken);

      await this.authRole();
    }

    return response;
  }

  /** Requests a fresh verification link. Backend always reports success to avoid leaking account existence. */
  async resendVerificationEmail(email: string): Promise<any> {
    const result: any = await this.apollo.mutate({
      mutation: RESEND_VERIFICATION_EMAIL,
      variables: { email }
    });

    const response = result?.data?.resendVerificationEmail;
    this.notificationService.successMessage(
      response?.response?.message || 'If that email needs verification, we\'ve sent a new link.'
    );

    return response;
  }

  /** Requests a password-reset code by email. Backend always reports success to avoid leaking account existence. */
  async requestPasswordReset(email: string): Promise<any> {
    const result: any = await this.apollo.mutate({
      mutation: REQUEST_PASSWORD_RESET,
      variables: { email }
    });

    return result?.data?.requestPasswordReset;
  }

  /** Validates the emailed code and sets a new password — on success this logs the user straight in, same as verifyEmail(). */
  async confirmPasswordReset(email: string, code: string, newPassword: string): Promise<any> {
    const result: any = await this.apollo.mutate({
      mutation: CONFIRM_PASSWORD_RESET,
      variables: { email, code, newPassword }
    });

    const response = result?.data?.confirmPasswordReset;

    if (response?.token) {
      const cleanToken = response.token.startsWith('Bearer ')
        ? response.token.replace('Bearer ', '')
        : response.token;

      this.storageService.set('currentClient', cleanToken);
      this.storageService.set('refreshToken', response.refreshToken);

      await this.authRole();
    }

    return response;
  }

  register(formData: any) {
    this.subscriptions.add(
      this.http.post(environment.SERVER_URL + '/auth/register', formData)
        .subscribe(() => {
          if (this.alreadyLoggedIn()) {
            this.logout();
            window.location.reload();
          } else {
             this.notificationService.successMessage(
                'Account created successfully, Check your email to get Access details'
              );
            this.openSnackbar('Account created successfully, Check your email to get Access details');
          }
          this.dialog.closeAll();
        })
    );
  }

  async generateResetLink(formData: { username: string }): Promise<AuthTokenModel | null> {
    console.log(formData, 'all data are being saved');
    const body = { username: formData };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });

    try {
      const response = await firstValueFrom(
        this.http.post<AuthTokenModel>(
          `${environment.SERVER_URL}/auth/forgot-password`,
          body,
          { headers }
        )
      );

      this.openSnackbar('Reset link sent to your e-mail. Check your email for access details');
      this.router.navigate(['', 'login']);
      return response as AuthTokenModel;
    } catch (error: any) {
      // console.error('Error response:', error);
      this.openSnackbar(error?.error?.message || 'Something went wrong');
      return null;
    }
  }




  alreadyLoggedIn(): boolean {
    return !!this.storageService.get('currentClient');
  }

  public clearStore(){
    this.storageService.clear();
    window.sessionStorage.clear();
    this.router.navigateByUrl('/login');
  }

  public collectFailedRequest(err): void {
    this.errorCode = err.status;
    this.errorDescription = err.reason;
    // 400 Bad Request — this fires for any failed request app-wide (REST
    // or GraphQL), not just login, so it can't assume "wrong credentials"
    // (that's actually a 401, handled below). Show the backend's real
    // message when there is one, otherwise a neutral fallback.
    if (this.errorCode === 400) {
      this.openSnackbar(this.errorDescription || 'Request failed. Please check your input and try again.');
    }

    if (this.errorCode === 401) {
      this.clearStore();
      if (this.errorDescription !== null){
        this.openSnackbar(this.errorDescription);
      }else {
        this.openSnackbar('Hauna Ruhusa ya Kuingia Kwenye Mfumo, Wasiliana na Afisa Tehema Kwa Msaada zaidi');
      }
    }
    if (this.errorCode === 404) {
      const message = 'Service Temporarily Unavailable';
      const action = 'Dismiss';
      this.snackbar.open(message, action, {
        duration: 5000
      });
    }
    if (this.errorCode === 500) {
      if (this.errorDescription !== null){
        this.openSnackbar(this.errorDescription);
      }else {
        this.snackbar.open('Something went wrong in our end.', 'Dismiss', {duration: 5000});
      }
    }
    if (this.errorCode === 504) {
      const message = 'Service Temporarily Unavailable';
      const action = 'Dismiss';
      this.snackbar.open(message, action, {
        duration: 5000
      });
    }
  }

  openSnackbar(message, closeText = 'Dismis', duration = 5000, verticalPosition: any = 'top', horizontalPosition: any = 'center') {
    this.snackbar.open(message, closeText, {
      duration,
      verticalPosition,
      horizontalPosition
    });
  }




  // getToken(): string | null {
  //   return localStorage.getItem('currentClient');
  // }
  // getToken(): string | null {
  //   const token = localStorage.getItem('currentClient');
  //   if (!token) return null;
  //
  //   // Strip Bearer prefix if any
  //   return token.startsWith('Bearer ') ? token.replace('Bearer ', '') : token;
  // }
  getToken(): string | null {
    const token = this.storageService.get<string>('currentClient');
    if (!token) return null;
    return token.startsWith('Bearer ') ? token.replace('Bearer ', '') : token;
  }

  // private decodeToken(token: string): any | null {
  //   try {
  //     const payload = token.split('.')[1];
  //     const decoded = JSON.parse(atob(payload));
  //     return decoded;
  //   } catch (error) {
  //     console.error('Invalid token:', error);
  //     return null;
  //   }
  // }
  private decodeToken(token: string): any | null {
    console.log('Raw token:', token); // <--- add this
    try {
      if (!token || typeof token !== 'string') return null;

      if (token.startsWith('Bearer ')) token = token.replace('Bearer ', '');
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('decodeToken: Token is not a valid JWT structure');
        return null;
      }

      const payload = parts[1];
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded;
    } catch (error) {
      console.error('Invalid token during decode:', error);
      return null;
    }
  }



  isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    // Convert expiration time from seconds to milliseconds
    const expiryTime = decoded.exp * 1000;
    return Date.now() > expiryTime;
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    return token != null && !this.isTokenExpired(token);
  }

  /**
   * True if there's a session worth keeping the user in — either the access
   * token is still fresh, or it was expired but the refresh token was able
   * to mint a new one. The access token only lives 30 minutes, so relying
   * on isLoggedIn() alone would treat a returning user (e.g. back after
   * lunch, well within their multi-day refresh token) as logged out.
   * Used by AuthGuard before killing a session outright, and by the landing
   * page before showing the marketing pitch to someone who's already
   * logged in.
   */
  async hasValidSession(): Promise<boolean> {
    if (this.isLoggedIn()) {
      return true;
    }

    const refreshToken = this.storageService.get('refreshToken');
    if (!refreshToken) {
      return false;
    }

    try {
      await this.refreshAccessToken();
      return true;
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}


const GET_CURRENT_USER_PROFILE = gql`query GetCurrentUserInfo {
  getCurrentUserInfo {
    userId
    username
    email
    firstName
    lastName
    isActive
    isStaff
    dateJoined
    shopUuid
    shopName
    shopRole
    shopIsActive
    shopJoinedAt
    isCustomer
    customerUuid
    customerPhone
    customerAddress
    totalOrders
    totalSpent
    permissionCodenames
    roleUuids
  }
}`
